/**
 * TransactionsTab — server-side paginated Xero journal-line browser.
 *
 * Source of truth: XeroJournalLine (all double-entry postings).
 * Filters: date range, account type, account code, source type,
 * contact, amount range, free-text search.
 */
import {DownloadIcon} from "lucide-react";
import {useCallback, useEffect, useMemo, useState} from "react";

import {Badge} from "@components/ui/badge";
import {Button} from "@components/ui/button";
import {Input} from "@components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@components/ui/table";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@components/ui/tooltip";
import {
    type LedgerFilters,
    type LedgerTransaction,
    useVNextTransactions,
} from "@hooks/use-vnext";

// ── Constants ──

const ACCOUNT_TYPE_OPTIONS = [
    {value: "__all__", label: "All Types"},
    {value: "REVENUE", label: "Revenue"},
    {value: "SALES", label: "Sales"},
    {value: "OTHERINCOME", label: "Other Income"},
    {value: "DIRECTCOSTS", label: "Direct Costs"},
    {value: "EXPENSE", label: "Expense"},
    {value: "OVERHEADS", label: "Overheads"},
    {value: "DEPRECIATN", label: "Depreciation"},
    {value: "BANK", label: "Bank"},
    {value: "CURRENT", label: "Current Asset"},
    {value: "FIXED", label: "Fixed Asset"},
    {value: "CURRLIAB", label: "Current Liability"},
    {value: "TERMLIAB", label: "Term Liability"},
    {value: "EQUITY", label: "Equity"},
];

const SOURCE_TYPE_OPTIONS = [
    {value: "__all__", label: "All Sources"},
    {value: "ACCREC", label: "Invoices (AR)"},
    {value: "ACCPAY", label: "Bills (AP)"},
    {value: "CASHREC", label: "Cash Received"},
    {value: "CASHPAID", label: "Cash Paid"},
    {value: "MANJOURNAL", label: "Manual Journal"},
    {value: "TRANSFER", label: "Transfer"},
];

const PAGE_SIZES = [50, 100, 200, 500];

// ── Helpers ──

function formatCurrency(value: number): string {
    return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
        minimumFractionDigits: 2,
    }).format(value);
}

function formatDate(iso: string): string {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-AU", {day: "2-digit", month: "short", year: "numeric"});
}

function sourceTypeBadge(sourceType: string): "default" | "secondary" | "outline" {
    if (sourceType === "ACCREC" || sourceType === "CASHREC") return "default";
    if (sourceType === "ACCPAY" || sourceType === "CASHPAID") return "secondary";
    return "outline";
}

// ── Filter Bar ──

interface FilterBarProps {
    filters: LedgerFilters;
    onChange: (f: LedgerFilters) => void;
}

function FilterBar({filters, onChange}: FilterBarProps) {
    const update = (patch: Partial<LedgerFilters>) => {
        onChange({...filters, ...patch, page: 1});
    };

    return (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 items-end">
            {/* Search */}
            <div className="col-span-2">
                <label className="text-xs text-muted-foreground mb-1 block">Search</label>
                <Input
                    placeholder="Contact, description, account, reference..."
                    value={filters.search ?? ""}
                    onChange={(e) => update({search: e.target.value})}
                    className="h-8 text-xs"
                />
            </div>

            {/* Date from */}
            <div>
                <label className="text-xs text-muted-foreground mb-1 block">From</label>
                <Input
                    type="date"
                    value={filters.date_from ?? ""}
                    onChange={(e) => update({date_from: e.target.value})}
                    className="h-8 text-xs"
                />
            </div>

            {/* Date to */}
            <div>
                <label className="text-xs text-muted-foreground mb-1 block">To</label>
                <Input
                    type="date"
                    value={filters.date_to ?? ""}
                    onChange={(e) => update({date_to: e.target.value})}
                    className="h-8 text-xs"
                />
            </div>

            {/* Account type */}
            <div>
                <label className="text-xs text-muted-foreground mb-1 block">Account Type</label>
                <Select
                    value={filters.account_type ?? "__all__"}
                    onValueChange={(v) =>
                        update({account_type: v === "__all__" ? undefined : v})
                    }
                >
                    <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {ACCOUNT_TYPE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                                {o.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Source type */}
            <div>
                <label className="text-xs text-muted-foreground mb-1 block">Source</label>
                <Select
                    value={filters.source_type ?? "__all__"}
                    onValueChange={(v) =>
                        update({source_type: v === "__all__" ? undefined : v})
                    }
                >
                    <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {SOURCE_TYPE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                                {o.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Account code */}
            <div>
                <label className="text-xs text-muted-foreground mb-1 block">Account Code</label>
                <Input
                    placeholder="e.g. 200"
                    value={filters.account_code ?? ""}
                    onChange={(e) => update({account_code: e.target.value})}
                    className="h-8 text-xs"
                />
            </div>

            {/* Contact */}
            <div>
                <label className="text-xs text-muted-foreground mb-1 block">Contact</label>
                <Input
                    placeholder="Name..."
                    value={filters.contact ?? ""}
                    onChange={(e) => update({contact: e.target.value})}
                    className="h-8 text-xs"
                />
            </div>

            {/* Min amount */}
            <div>
                <label className="text-xs text-muted-foreground mb-1 block">Min $</label>
                <Input
                    type="number"
                    placeholder="0"
                    value={filters.min_amount ?? ""}
                    onChange={(e) => update({min_amount: e.target.value})}
                    className="h-8 text-xs"
                />
            </div>

            {/* Max amount */}
            <div>
                <label className="text-xs text-muted-foreground mb-1 block">Max $</label>
                <Input
                    type="number"
                    placeholder="∞"
                    value={filters.max_amount ?? ""}
                    onChange={(e) => update({max_amount: e.target.value})}
                    className="h-8 text-xs"
                />
            </div>

            {/* Clear */}
            <div className="flex items-end">
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() =>
                        onChange({page: 1, page_size: filters.page_size, sort: filters.sort})
                    }
                >
                    Clear Filters
                </Button>
            </div>
        </div>
    );
}

// ── Pagination ──

interface PaginationProps {
    page: number;
    totalPages: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
}

function Pagination({page, totalPages, pageSize, totalCount, onPageChange, onPageSizeChange}: PaginationProps) {
    return (
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2">
            <div className="flex items-center gap-2">
                <span>{totalCount.toLocaleString()} journal lines</span>
                <Select
                    value={String(pageSize)}
                    onValueChange={(v) => onPageSizeChange(Number(v))}
                >
                    <SelectTrigger className="h-7 w-20 text-xs">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {PAGE_SIZES.map((s) => (
                            <SelectItem key={s} value={String(s)}>
                                {s}/page
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-7 text-xs px-2" disabled={page <= 1} onClick={() => onPageChange(1)}>First</Button>
                <Button variant="outline" size="sm" className="h-7 text-xs px-2" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>Prev</Button>
                <span className="px-2">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" className="h-7 text-xs px-2" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>Next</Button>
                <Button variant="outline" size="sm" className="h-7 text-xs px-2" disabled={page >= totalPages} onClick={() => onPageChange(totalPages)}>Last</Button>
            </div>
        </div>
    );
}

// ── Transaction Row ──

function TransactionRow({txn}: {txn: LedgerTransaction}) {
    return (
        <TableRow>
            <TableCell className="text-xs whitespace-nowrap">{formatDate(txn.date)}</TableCell>
            <TableCell className="text-xs font-mono">{txn.journal_number ?? "—"}</TableCell>
            <TableCell className="text-xs">
                <Badge variant={sourceTypeBadge(txn.source_type)} className="text-[10px]">
                    {txn.source_type || "—"}
                </Badge>
            </TableCell>
            <TableCell className="text-xs font-mono">{txn.account_code || "—"}</TableCell>
            <TableCell className="text-xs max-w-[140px] truncate">
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span>{txn.account_name || "—"}</span>
                        </TooltipTrigger>
                        {txn.account_name && (
                            <TooltipContent>
                                {txn.account_name} ({txn.account_type})
                            </TooltipContent>
                        )}
                    </Tooltip>
                </TooltipProvider>
            </TableCell>
            <TableCell className="text-xs max-w-[160px] truncate">
                {txn.description || txn.reference || "—"}
            </TableCell>
            <TableCell className="text-xs max-w-[140px] truncate">{txn.contact_name || "—"}</TableCell>
            <TableCell className="text-xs font-mono text-right text-green-700">
                {txn.debit > 0 ? formatCurrency(txn.debit) : ""}
            </TableCell>
            <TableCell className="text-xs font-mono text-right text-red-600">
                {txn.credit > 0 ? formatCurrency(txn.credit) : ""}
            </TableCell>
            <TableCell className="text-xs font-mono text-right font-medium">
                {formatCurrency(txn.net_amount)}
            </TableCell>
        </TableRow>
    );
}

// ── Main Component ──

export function TransactionsTab({
    workspaceId,
    initialFilters,
}: {
    workspaceId: string;
    scenarioId: string;
    initialFilters?: LedgerFilters;
}) {
    const [filters, setFilters] = useState<LedgerFilters>({
        page: 1,
        page_size: 100,
        sort: "-date",
        ...initialFilters,
    });
    const [showFilterBanner, setShowFilterBanner] = useState(!!initialFilters);

    useEffect(() => {
        if (initialFilters) {
            setFilters(prev => ({...prev, ...initialFilters, page: 1}));
            setShowFilterBanner(true);
        }
    }, [initialFilters]);

    const {data, isLoading, error} = useVNextTransactions(workspaceId, filters);

    const transactions = useMemo(() => data?.transactions ?? [], [data?.transactions]);
    const page = data?.page ?? 1;
    const pageSize = data?.page_size ?? 100;
    const totalCount = data?.total_count ?? 0;
    const totalPages = data?.total_pages ?? 0;
    const totals = data?.totals ?? {sum_debit: 0, sum_credit: 0, sum_net: 0, count: 0};

    const handlePageChange = useCallback(
        (p: number) => setFilters((f) => ({...f, page: p})),
        [],
    );

    const handlePageSizeChange = useCallback(
        (size: number) => setFilters((f) => ({...f, page_size: size, page: 1})),
        [],
    );

    const handleSort = useCallback(
        (field: string) => {
            setFilters((f) => {
                const current = f.sort ?? "-date";
                const newSort = current === field ? `-${field}` : current === `-${field}` ? field : field;
                return {...f, sort: newSort, page: 1};
            });
        },
        [],
    );

    const handleExportCsv = useCallback(() => {
        if (transactions.length === 0) return;
        const headers = ["Date", "Journal#", "Source", "Account Code", "Account Name", "Description", "Contact", "Debit", "Credit", "Net"];
        const csvRows = [headers.join(",")];
        for (const txn of transactions) {
            const row = [
                txn.date,
                txn.journal_number ?? "",
                txn.source_type,
                txn.account_code ?? "",
                `"${(txn.account_name ?? "").replace(/"/g, '""')}"`,
                `"${(txn.description ?? txn.reference ?? "").replace(/"/g, '""')}"`,
                `"${(txn.contact_name ?? "").replace(/"/g, '""')}"`,
                txn.debit > 0 ? txn.debit.toFixed(2) : "",
                txn.credit > 0 ? txn.credit.toFixed(2) : "",
                txn.net_amount.toFixed(2),
            ];
            csvRows.push(row.join(","));
        }
        const blob = new Blob([csvRows.join("\n")], {type: "text/csv;charset=utf-8;"});
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `transactions_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }, [transactions]);

    const sortIndicator = (field: string) => {
        const current = filters.sort ?? "-date";
        if (current === field) return " ↑";
        if (current === `-${field}`) return " ↓";
        return "";
    };

    if (error) {
        return (
            <div className="p-6 text-center text-sm text-destructive">
                Failed to load transactions: {error}
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {/* Filter banner from drilldown */}
            {showFilterBanner && (
                <div className="flex items-center gap-2 rounded-md border border-[#5BF6C8] bg-[#209DB3]/5 px-3 py-1.5 text-xs">
                    <span className="font-medium text-[#209DB3]">Filtered from statement drilldown</span>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 px-1.5 text-xs"
                        onClick={() => {
                            setShowFilterBanner(false);
                            setFilters({page: 1, page_size: filters.page_size, sort: filters.sort});
                        }}
                    >
                        Clear filters
                    </Button>
                </div>
            )}

            {/* Filter bar */}
            <FilterBar filters={filters} onChange={setFilters} />

            {/* Export CSV */}
            <div className="flex justify-end">
                <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={handleExportCsv}
                    disabled={transactions.length === 0}
                >
                    <DownloadIcon className="size-3 mr-1" />
                    Export Page CSV
                </Button>
            </div>

            {/* Summary */}
            <div className="flex items-center gap-6 text-xs text-muted-foreground">
                <span>Debits: <span className="text-green-700 font-medium">{formatCurrency(totals.sum_debit)}</span></span>
                <span>Credits: <span className="text-red-600 font-medium">{formatCurrency(totals.sum_credit)}</span></span>
                <span>Net: <span className="font-medium">{formatCurrency(totals.sum_net)}</span></span>
            </div>

            {/* Table */}
            <div className="border rounded-md overflow-auto max-h-[calc(100vh-350px)]">
                <Table>
                    <TableHeader className="sticky top-0 bg-background z-10">
                        <TableRow>
                            <TableHead className="text-xs cursor-pointer select-none" onClick={() => handleSort("date")}>
                                Date{sortIndicator("date")}
                            </TableHead>
                            <TableHead className="text-xs">J#</TableHead>
                            <TableHead className="text-xs cursor-pointer select-none" onClick={() => handleSort("source_type")}>
                                Source{sortIndicator("source_type")}
                            </TableHead>
                            <TableHead className="text-xs cursor-pointer select-none" onClick={() => handleSort("account_code")}>
                                Code{sortIndicator("account_code")}
                            </TableHead>
                            <TableHead className="text-xs">Account</TableHead>
                            <TableHead className="text-xs">Description</TableHead>
                            <TableHead className="text-xs cursor-pointer select-none" onClick={() => handleSort("contact_name")}>
                                Contact{sortIndicator("contact_name")}
                            </TableHead>
                            <TableHead className="text-xs text-right">Debit</TableHead>
                            <TableHead className="text-xs text-right">Credit</TableHead>
                            <TableHead className="text-xs text-right cursor-pointer select-none" onClick={() => handleSort("net_amount")}>
                                Net{sortIndicator("net_amount")}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading && transactions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} className="text-center text-xs text-muted-foreground py-8">
                                    Loading journal lines...
                                </TableCell>
                            </TableRow>
                        ) : transactions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} className="text-center text-xs text-muted-foreground py-8">
                                    No journal lines found. Sync your Xero data first.
                                </TableCell>
                            </TableRow>
                        ) : (
                            transactions.map((txn) => (
                                <TransactionRow key={txn.id} txn={txn} />
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            {totalPages > 0 && (
                <Pagination
                    page={page}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    totalCount={totalCount}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            )}
        </div>
    );
}
